import { useQuery } from '@tanstack/react-query';
import {
  sparkByPersona,
  PERSONA_SPARK_QUERY_KEYS,
  mapPersonaToSpark,
  type ParsedSpark,
} from '@/entities/persona-spark';
import { getAll } from '@/entities/persona';
import { AudienceSparkSourceDto } from '@inference.engine/api-client/web';
import { logger } from '@/lib/utils/logger';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import type { UsePersonaSparksOptions } from './usePersonaSparks.types';

export type { UsePersonaSparksOptions, ParsedSpark } from './usePersonaSparks.types';

/**
 * Custom hook to fetch and parse persona sparks.
 * Combines persona data with their generated sparks and sources.
 *
 * The `getAgents` function is INJECTED via options (Springboard dependency injection)
 * to keep this slice free of cross-feature OUT to `@/features/agents`.
 */
export const usePersonaSparks = ({
  workspaceId,
  enabled = true,
  getAgents,
}: UsePersonaSparksOptions) => {
  const agentsQuery = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => getAgents(),
    staleTime: 5 * 60 * 1000,
  });

  const hasAudienceAgent = agentsQuery.data?.some((a) => a.id === SPECIAL_AGENT_IDS.AUDIENCE_FLOW);

  const sparksQuery = useQuery({
    queryKey: PERSONA_SPARK_QUERY_KEYS.all(workspaceId ?? ''),
    queryFn: async (): Promise<ParsedSpark[]> => {
      if (!workspaceId) return [];
      const personas = await getAll(workspaceId);
      if (!personas.length) return [];

      const sparksPromises = personas.map(async (persona) => {
        try {
          const spark = await sparkByPersona(persona.id);

          if (!spark?.sources || !Array.isArray(spark.sources) || spark.sources.length === 0) {
            return [];
          }

          const maxLength = 250;
          const separator = '\n\n';
          let combinedText = '';

          for (const source of spark.sources) {
            const newText = combinedText ? combinedText + separator + source.text : source.text;
            if (newText.length > maxLength) break;
            combinedText = newText;
          }

          const combinedSource: AudienceSparkSourceDto = {
            ...spark.sources[0],
            text: combinedText || spark.sources[0].text,
          };
          return [mapPersonaToSpark(persona, spark, combinedSource)];
        } catch (error) {
          logger.error(`Failed to fetch spark for persona ${persona.id}:`, error);
          return [];
        }
      });

      const results = await Promise.all(sparksPromises);
      return results.flat();
    },
    enabled: enabled && !!workspaceId && !!hasAudienceAgent,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    sparks: sparksQuery.data ?? [],
    isLoading: agentsQuery.isLoading || (hasAudienceAgent && sparksQuery.isLoading),
    isFetching: agentsQuery.isFetching || sparksQuery.isFetching,
    error: agentsQuery.error || sparksQuery.error,
    isError: agentsQuery.isError || sparksQuery.isError,
    isSuccess: agentsQuery.isSuccess && (!hasAudienceAgent || sparksQuery.isSuccess),
    refetch: async () => {
      await agentsQuery.refetch();
      await sparksQuery.refetch();
    },
  };
};
